/**
 * Runtime WebAuthn: una ceremonia activa, timeout y AbortSignal.
 * Paridad con shell WMS; no altera payloads del portal.
 */

const DEFAULT_TIMEOUT_MS = 120_000;
const MIN_TIMEOUT_MS = 1_000;
const MAX_TIMEOUT_MS = 300_000;

let activeCeremony = null;

export class WebAuthnRuntimeError extends Error {
  constructor(code, message, cause) {
    super(message, cause ? { cause } : undefined);
    this.name = 'WebAuthnRuntimeError';
    this.code = code;
  }
}

export function supportsWebAuthnRuntime() {
  return Boolean(
    typeof window !== 'undefined'
      && window.isSecureContext
      && window.PublicKeyCredential
      && typeof navigator !== 'undefined'
      && navigator.credentials
      && typeof navigator.credentials.create === 'function'
      && typeof navigator.credentials.get === 'function'
  );
}

export function hasActiveWebAuthnCeremony() {
  return activeCeremony !== null;
}

export function cancelActiveWebAuthnCeremony() {
  activeCeremony?.abortController.abort();
}

export async function createWebAuthnCredential(publicKey) {
  return runCeremony('create', publicKey);
}

export async function getWebAuthnCredential(publicKey) {
  return runCeremony('get', publicKey);
}

async function runCeremony(operation, publicKey) {
  if (!supportsWebAuthnRuntime()) {
    throw new WebAuthnRuntimeError(
      'WEBAUTHN_UNAVAILABLE',
      'Este dispositivo o navegador no permite usar una passkey. Podés ingresar con contraseña.'
    );
  }
  if (!publicKey || typeof publicKey !== 'object' || Array.isArray(publicKey)) {
    throw new WebAuthnRuntimeError(
      'WEBAUTHN_OPTIONS_INVALID',
      'El servidor devolvió opciones WebAuthn inválidas.'
    );
  }
  if (activeCeremony) {
    throw new WebAuthnRuntimeError(
      'WEBAUTHN_OPERATION_IN_PROGRESS',
      'Ya existe una operación de passkey en curso.'
    );
  }

  const abortController = new AbortController();
  const requestId = globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const timeoutMs = normalizedTimeout(publicKey.timeout);
  const timeoutId = window.setTimeout(() => abortController.abort(), timeoutMs);
  activeCeremony = { requestId, abortController };

  try {
    const request = { publicKey, signal: abortController.signal };
    const credential = operation === 'create'
      ? await navigator.credentials.create(request)
      : await navigator.credentials.get(request);

    if (!credential) {
      throw new WebAuthnRuntimeError(
        'WEBAUTHN_EMPTY_RESPONSE',
        'El proveedor de credenciales no devolvió una respuesta válida.'
      );
    }
    return credential;
  } catch (error) {
    if (error instanceof WebAuthnRuntimeError) throw error;
    throw mapRuntimeDomError(error);
  } finally {
    window.clearTimeout(timeoutId);
    if (activeCeremony?.requestId === requestId) activeCeremony = null;
  }
}

function normalizedTimeout(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return DEFAULT_TIMEOUT_MS;
  return Math.min(Math.max(numeric, MIN_TIMEOUT_MS), MAX_TIMEOUT_MS);
}

function mapRuntimeDomError(error) {
  switch (error?.name) {
    case 'AbortError':
      return new WebAuthnRuntimeError(
        'WEBAUTHN_CANCELLED_OR_TIMEOUT',
        'La operación fue cancelada o venció. Intentá nuevamente.',
        error
      );
    case 'NotAllowedError':
      return new WebAuthnRuntimeError(
        'WEBAUTHN_NOT_ALLOWED',
        'No se confirmó el acceso con la passkey.',
        error
      );
    case 'NotSupportedError':
      return new WebAuthnRuntimeError(
        'WEBAUTHN_NOT_SUPPORTED',
        'El dispositivo no tiene un proveedor de credenciales compatible.',
        error
      );
    case 'InvalidStateError':
      return new WebAuthnRuntimeError(
        'WEBAUTHN_INVALID_STATE',
        'La credencial ya existe o no puede utilizarse en este estado.',
        error
      );
    case 'SecurityError':
      return new WebAuthnRuntimeError(
        'WEBAUTHN_SECURITY_ERROR',
        'El origen o RP ID no está autorizado para usar passkeys.',
        error
      );
    case 'NetworkError':
      return new WebAuthnRuntimeError(
        'WEBAUTHN_PROVIDER_ERROR',
        'El proveedor de credenciales no pudo completar la operación.',
        error
      );
    default:
      return new WebAuthnRuntimeError(
        'WEBAUTHN_UNKNOWN_ERROR',
        'No fue posible completar la operación con passkey.',
        error
      );
  }
}
